import { Box, Card, CardContent, useTheme, Alert, CircularProgress, Typography, Button } from "@mui/material";
import {
    LazzyLoading,
} from "../../../common/contentType/contentTypeData";
import { useEffect, useState } from "react";
import TypographyWithMore from "../../../common/contentType/TypographyWithMore";
import { createSession } from "../../../_api/real-play";
import { FullscreenWrapper } from "../fullscreen";

const RealPlay = ({ content, user }) => {
    const theme = useTheme();
    const { name, description, content: contentData } = content?.allContent;
    const [isIframeReady, setIsIframeReady] = useState(false);
    const [session, setSession] = useState(null);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [sessionError, setSessionError] = useState(null);

    let { scenarioId, characterId } = JSON.parse(contentData);

    const handleCreateSession = async () => {
        if (!scenarioId) return;

        setIsLoadingSession(true);
        setSessionError(null);

        try {
            const res = await createSession({ scenarioId, characterId });
            console.log("session created", res);
            setSession(res?.session);
        } catch (err) {
            console.error("Failed to create session:", err);
            setSessionError(err.message || "Failed to create session. Please try again.");
        } finally {
            setIsLoadingSession(false);
        }
    };

    const renderContent = () => {
        // Show initial state with button to start session
        if (!session && !isLoadingSession && !sessionError) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    gap={2}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleCreateSession}
                        sx={{
                            px: 4,
                            py: 2,
                            fontSize: '1.1rem',
                            borderRadius: '12px',
                            textTransform: 'none',
                        }}
                    >
                        Let's get details of session
                    </Button>
                </Box>
            );
        }

        // Show loading state
        if (isLoadingSession) {
            return (
                <LoadingCompnent />
            );
        }

        // Show error state
        if (sessionError) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    gap={2}
                    p={3}
                >
                    <Alert
                        severity="error"
                        sx={{ width: "100%", maxWidth: "500px" }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleCreateSession}
                                disabled={isLoadingSession}
                            >
                                Retry
                            </Button>
                        }
                    >
                        <Typography variant="body2">
                            {sessionError}
                        </Typography>
                    </Alert>
                </Box>
            );
        }

        // Show content when session is ready
        if (session) {
            return (
                <FullscreenWrapper
                    containerProps={{
                        normalHeight: "100%",
                        fullscreenHeight: "100vh"
                    }}
                    buttonProps={{
                        position: "top-right",
                        visible: true
                    }}
                >
                    {!isIframeReady && <LoadingCompnent />}
                    <iframe
                        onLoad={() => setIsIframeReady(true)}
                        style={{
                            opacity: isIframeReady ? 1 : 0,
                            height: "98.5%",
                            borderRadius: "1.875rem",
                            width: "100%",
                        }}
                        src={`https://realplay.stickball.biz/dashboard/practice/${session.id}?characterId=${characterId}&paramCount=1`}
                        frameBorder="0"
                        allow="microphone; camera"
                    ></iframe>
                </FullscreenWrapper>
            );
        }

        // Fallback state
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
            >
                <Typography variant="body1" color="text.secondary">
                    No session data available
                </Typography>
            </Box>
        );
    };

    return (
        <Box width="100%" height={{ xs: "calc(100vh - 150px)", md: "calc(100vh - 150px)" }} sx={{ overflowY: "auto", mt: "1rem" }}>
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1000,
                    width: '50px',
                    height: '100vh',
                }}
            ></Box>
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    zIndex: 1000,
                    width: '50px',
                    height: '100vh',
                }}
            ></Box>
            <TypographyWithMore
                variant="h2"
                color="primary"
                data={name}
                textLength={18}
                lines={2}
                textAlign="center"
                width="100%"
            />
            <TypographyWithMore
                variant="body1"
                color="text"
                data={description}
                textLength={40}
                lines={2}
                textAlign="center"
                width="100%"
            />
            <Card sx={{ boxShadow: "none" }}>
                <CardContent
                    sx={{
                        p: 1.5,
                        borderRadius: "1.8rem",
                        border: { sm: `1px solid ${theme.palette.common.disabled}`, xs: "none" },
                        mt: "1rem",
                        height: { xs: "calc(100vh - 140px)", md: "calc(100vh - 260px)" },
                        overflowY: "auto",
                        '&:last-child': {
                            pb: 0
                        }
                    }}>
                    {renderContent()}
                </CardContent>
            </Card>
        </Box>
    );
};

export default RealPlay;


const LoadingCompnent = () => {
    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            gap={2}
        >
            <CircularProgress size={50} />
            <Typography variant="body1" color="text.secondary">
                Setting up your real play session...
            </Typography>
        </Box>
    )
}